import { useEffect, useState } from "react";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import {
  ArrowRight,
  Send,
  Wallet,
  CreditCard,
  Gift,
  LifeBuoy,
  Building2,
  ChevronRight,
  Plus,
  Sparkles,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import {
  GrowthIllustration,
  EcosystemIllustration,
  AnywhereIllustration,
  ProcessIllustration,
  ContactIllustration,
} from "@/components/illustrations/FintechIllustrations";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { API_BASE } from "@/lib/site";
import { getTrackingPayload } from "@/lib/utm";
import { initTracking, trackConversion, pushLeadDataLayer } from "@/lib/tracking";

const SECTIONS = [
  {
    id: "account-information",
    eyebrow: "01 — Account Information",
    icon: Building2,
    Illustration: ProcessIllustration,
    title: "Account Information",
    blurb:
      "Find helpful information about merchant accounts, payment processing setup, required business details, approvals, statements, deposits, and account support.",
    imagePosition: "right",
    qa: [
      {
        q: "What is a merchant account?",
        a: "A merchant account is the financial relationship that allows a business to accept card payments. Funds from card transactions are processed, settled, and deposited into your business bank account through this account. Swipetronics helps merchants set one up that fits how their business actually operates.",
      },
      {
        q: "What information is needed to set up a merchant account?",
        a: "Typical requirements include legal business name, ownership details, EIN or SSN, business bank account information, processing volume estimates, average ticket size, and a recent processing statement if available. Some industries may require additional documentation.",
      },
      {
        q: "How long does account approval take?",
        a: "Most accounts are approved within one to three business days. Some industries, higher-risk categories, or accounts missing documentation can take longer. Swipetronics walks you through what to expect at each step.",
      },
      {
        q: "When do deposits reach my bank account?",
        a: "Most batches deposit next business day. Cut-off times and deposit speed depend on your processor, your bank, and how the batch is closed. Same-day and faster funding options may be available for eligible merchants.",
      },
      {
        q: "What is a merchant statement?",
        a: "A merchant statement is the monthly document that breaks down processing volume, fees, interchange, assessments, and deposits. Reading one carefully is the only reliable way to know your true effective rate — the total cost divided by total card volume.",
      },
      {
        q: "Can Swipetronics review my current statement?",
        a: "Yes. Send us a recent statement and a Swipetronics specialist will walk through it with you, line by line, and show where savings may exist. There is no fee for this review and no pressure to switch.",
      },
    ],
  },
  {
    id: "payment-processing",
    eyebrow: "02 — Payment Processing",
    icon: CreditCard,
    Illustration: AnywhereIllustration,
    title: "Payment Processing",
    blurb:
      "This section explains common payment processing topics, including card acceptance, terminals, online payments, mobile payments, transaction security, and choosing the right setup for your business.",
    imagePosition: "left",
    qa: [
      {
        q: "What types of payments can my business accept?",
        a: "Swipetronics supports credit and debit cards, contactless (tap), chip (EMV), magnetic stripe, mobile wallets like Apple Pay and Google Pay, online checkout, virtual terminal entry, and ACH bank transfers. The right mix depends on how your customers pay today and where you want to grow.",
      },
      {
        q: "Can I accept tap, chip, swipe, and mobile wallet payments?",
        a: "Yes. Modern Swipetronics terminals and POS systems support every common acceptance method at the counter — contactless tap, EMV chip insert, traditional swipe, and mobile wallets — so checkout stays fast no matter how a customer pays.",
      },
      {
        q: "What is the difference between credit and debit processing?",
        a: "Credit transactions are processed through the card networks and typically carry higher interchange. Debit transactions can run as PIN debit (over a debit network) or signature debit (over the credit network) and may have lower fees. The right routing depends on volume, ticket size, and your business type.",
      },
      {
        q: "Is payment processing secure?",
        a: "Swipetronics uses EMV chip technology, end-to-end encryption, and tokenization on every transaction. We also help merchants stay aligned with PCI requirements so cardholder data is protected from the moment a card is presented.",
      },
      {
        q: "What is a virtual terminal?",
        a: "A virtual terminal is a secure web-based screen that lets your team key in card payments from any browser. It is useful for phone orders, mail orders, invoicing, and any business that takes card-not-present transactions.",
      },
      {
        q: "Can I accept payments online?",
        a: "Yes. Swipetronics offers gateway integrations for shopping carts, custom websites, and invoicing platforms. Whether you need a checkout button, a hosted payment page, or a full e-commerce integration, we help match the right setup to your platform.",
      },
      {
        q: "Can Swipetronics help me choose the right terminal or POS system?",
        a: "Yes. We start with how your business actually operates — counter flow, ticket size, integrations, peak hours — then recommend the right hardware and software. We never push equipment you do not need.",
      },
      {
        q: "Can I switch from my current processor?",
        a: "Most merchants can. Swipetronics helps review your current setup, identify any contract or equipment-lease considerations, and plan a smooth cutover so the switch is as simple and disruption-free as possible.",
      },
    ],
  },
  {
    id: "cash-discount-dual-pricing",
    eyebrow: "03 — Cash Discount & Dual Pricing",
    icon: Wallet,
    Illustration: GrowthIllustration,
    title: "Cash Discount & Dual Pricing",
    blurb:
      "Learn how cash discount and dual pricing programs may help eligible businesses reduce or offset card acceptance costs when set up properly.",
    imagePosition: "right",
    qa: [
      {
        q: "What is a cash discount program?",
        a: "A cash discount program offers a price reduction to customers who pay with cash, while card-paying customers see the standard listed price. When implemented properly, it gives businesses a clear way to communicate pricing and may help offset card acceptance costs.",
      },
      {
        q: "What is dual pricing?",
        a: "Dual pricing displays two prices at the point of sale — one for cash and one for card. Customers see both prices clearly before choosing how to pay. Dual pricing aims for full transparency at checkout and on receipts.",
      },
      {
        q: "Is cash discount the same as a surcharge?",
        a: "No. A surcharge adds an extra fee on top of the listed price when a customer pays with a credit card. Cash discount and dual pricing structure pricing differently and follow different card brand and state rules. Swipetronics helps confirm which approach fits a specific business.",
      },
      {
        q: "Can every business use cash discount or dual pricing?",
        a: "Not always. Program availability depends on business type, card brand rules, state requirements, processor approval, and underwriting. Some industries and locations have specific limitations.",
      },
      {
        q: "Can debit cards be surcharged?",
        a: "Debit cards generally cannot be surcharged. Card brand and federal rules treat debit differently from credit, which is why setup matters. Swipetronics helps configure programs so debit acceptance is handled correctly.",
      },
      {
        q: "Are there rules businesses must follow?",
        a: "Yes. Card brand rules, federal regulations, and state-level requirements all play a role. These include posting requirements at the point of sale, receipt language, and how the pricing difference is presented. Swipetronics helps merchants set programs up in a compliant way.",
      },
      {
        q: "How does Swipetronics help set this up properly?",
        a: "We review your business type, check eligibility against card brand and state requirements, configure terminals and POS systems for the program, and provide signage and receipt language guidance. Program availability depends on business type, card brand rules, state requirements, processor approval, and underwriting.",
      },
    ],
  },
  {
    id: "free-pos-program",
    eyebrow: "04 — Free POS Program",
    icon: Gift,
    Illustration: EcosystemIllustration,
    title: "Free POS Program",
    blurb:
      "Eligible businesses may qualify for a free POS system when switching to Swipetronics. This section explains how eligibility works and what business owners should expect.",
    imagePosition: "left",
    qa: [
      {
        q: "How does the Free POS Program work?",
        a: "Eligible businesses receive POS hardware and software as part of their Swipetronics processing agreement. We review processing volume, business type, and equipment needs, then match the right system to the operation.",
      },
      {
        q: "Is the POS really free?",
        a: "Yes, qualified merchants receive the POS hardware at no upfront cost as part of their processing relationship. Eligibility, available equipment, and any optional add-ons are confirmed during the review. Program terms apply.",
      },
      {
        q: "What businesses may qualify?",
        a: "Restaurants, retailers, salons, convenience stores, service businesses, and other established merchants commonly qualify. Eligibility depends on processing volume, business type, time in business, and underwriting.",
      },
      {
        q: "What information is needed to check eligibility?",
        a: "Typically a recent merchant statement, basic business information, and a short conversation about how the business operates. Swipetronics handles the heavy lifting from there.",
      },
      {
        q: "Does the program include setup?",
        a: "Yes. Eligible merchants receive equipment configuration, onboarding, and training as part of the program so the team is ready on day one.",
      },
      {
        q: "Can I keep my current equipment?",
        a: "Sometimes. Many modern terminals and POS systems are compatible with Swipetronics processing. We tell you upfront whether keeping the current equipment makes sense or whether new hardware is the better path.",
      },
    ],
  },
  {
    id: "support-setup",
    eyebrow: "05 — Support & Setup",
    icon: LifeBuoy,
    Illustration: ContactIllustration,
    title: "Support & Setup",
    blurb:
      "Swipetronics helps businesses through setup, onboarding, equipment selection, and support after installation.",
    imagePosition: "right",
    qa: [
      {
        q: "Does Swipetronics help with installation?",
        a: "Yes. We configure equipment before it ships, walk through setup steps, and support the install end-to-end so the system is ready when your team is.",
      },
      {
        q: "Can setup be done remotely?",
        a: "Yes. Many setups are completed remotely with guided onboarding and pre-configured hardware. Remote setup is fast, low-disruption, and works well for most countertop, mobile, and virtual terminal deployments.",
      },
      {
        q: "Is on-site setup available?",
        a: "Yes. For larger POS rollouts, multi-location merchants, or businesses that prefer hands-on help, on-site setup and training can be arranged.",
      },
      {
        q: "What happens if I need support after setup?",
        a: "You get real people to talk to. Swipetronics provides ongoing phone and email support, plus account assistance, equipment troubleshooting, and help with any questions that come up after launch.",
      },
      {
        q: "Will my business have help switching providers?",
        a: "Yes. We review your existing setup, plan the transition, coordinate equipment cutover for off-peak hours, and brief your team so the switch feels organized and low-stress.",
      },
      {
        q: "How do I get started?",
        a: "Send us a recent statement or simply reach out. A Swipetronics specialist will review your current setup, explain your options in plain language, and recommend the right path forward.",
      },
    ],
  },
];

function FAQSection({ section, index }) {
  const Icon = section.icon;
  const Illustration = section.Illustration;
  const imageOnLeft = section.imagePosition === "left";

  const ImageBlock = (
    <Reveal variant={imageOnLeft ? "slide-left" : "slide-right"} delay={80}>
      <div className="relative lg:sticky lg:top-32">
        <div
          aria-hidden="true"
          className="absolute -inset-4 rounded-[40px] pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 70% 70% at 50% 50%, rgba(0,114,255,0.18) 0%, transparent 70%)",
          }}
        />
        <ParticleField
          density={24}
          connectDistance={110}
          className="absolute inset-0 w-full h-full rounded-[40px]"
        />
        <Illustration className="relative w-full h-auto max-w-[520px] mx-auto drop-shadow-[0_30px_60px_rgba(6,26,58,0.18)]" />
      </div>
    </Reveal>
  );

  const ContentBlock = (
    <Reveal variant={imageOnLeft ? "slide-right" : "slide-left"}>
      <div>
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-[var(--swipe-accent)] text-white flex items-center justify-center shadow-[0_12px_28px_-8px_rgba(0,114,255,0.55)]">
            <Icon className="w-7 h-7" strokeWidth={2} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--swipe-accent)]">
              {section.eyebrow}
            </div>
            <div className="mt-1 h-[3px] w-12 bg-gradient-to-r from-[var(--swipe-accent)] to-transparent rounded-full" />
          </div>
        </div>
        <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.1]">
          {section.title}
        </h2>
        <p className="mt-5 text-[16.5px] text-slate-600 leading-[1.75] max-w-xl">
          {section.blurb}
        </p>

        <AccordionPrimitive.Root
          type="single"
          collapsible
          className="mt-10 flex flex-col gap-3"
          data-testid={`faq-accordion-${section.id}`}
          defaultValue={`${section.id}-0`}
        >
          {section.qa.map((item, i) => (
            <AccordionPrimitive.Item
              key={item.q}
              value={`${section.id}-${i}`}
              className="group/item bg-white border border-line rounded-2xl overflow-hidden transition-all duration-300 hover:border-[var(--swipe-accent)] hover:shadow-[0_18px_36px_-22px_rgba(0,114,255,0.25)] data-[state=open]:border-[var(--swipe-accent)] data-[state=open]:shadow-[0_22px_48px_-22px_rgba(0,114,255,0.30)]"
            >
              <AccordionPrimitive.Header className="flex">
                <AccordionPrimitive.Trigger
                  className="group/trigger flex flex-1 items-center justify-between gap-5 px-6 py-5 text-left text-[16px] md:text-[17px] font-bold text-[var(--swipe-navy)] transition-colors hover:text-[var(--swipe-accent)] data-[state=open]:text-[var(--swipe-accent)] outline-none"
                  data-testid={`faq-q-${section.id}-${i}`}
                >
                  <span className="flex-1">{item.q}</span>
                  <span
                    aria-hidden="true"
                    className="shrink-0 w-10 h-10 rounded-lg bg-[var(--swipe-accent)] text-white flex items-center justify-center transition-all duration-300 group-hover/trigger:bg-[var(--swipe-navy)] group-data-[state=open]/item:bg-[var(--swipe-navy)] group-data-[state=open]/item:rotate-45 shadow-[0_8px_18px_-8px_rgba(0,114,255,0.55)]"
                  >
                    <Plus className="w-5 h-5" strokeWidth={2.5} />
                  </span>
                </AccordionPrimitive.Trigger>
              </AccordionPrimitive.Header>
              <AccordionPrimitive.Content className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                <div className="px-6 pb-6 pt-2">
                  <div className="border-t border-[var(--swipe-accent)]/15 pt-5 bg-[var(--swipe-accent-soft)]/40 -mx-6 px-6 -mb-6 pb-6 rounded-b-2xl">
                    <p className="text-[15.5px] text-slate-700 leading-[1.8] max-w-2xl">{item.a}</p>
                  </div>
                </div>
              </AccordionPrimitive.Content>
            </AccordionPrimitive.Item>
          ))}
        </AccordionPrimitive.Root>
      </div>
    </Reveal>
  );

  return (
    <section
      id={section.id}
      className={`section ${
        index === 0
          ? "bg-white"
          : index === 1
            ? "bg-[var(--swipe-muted)] border-y border-line"
            : index === 2
              ? "bg-[var(--swipe-accent-soft)]/40 border-y border-[var(--swipe-accent)]/15"
              : index === 3
                ? "bg-white"
                : "bg-[var(--swipe-muted)] border-y border-line"
      }`}
    >
      <div className="container-swipe">
        <div className="grid lg:grid-cols-[1.1fr,1fr] gap-12 lg:gap-20 items-start">
          {imageOnLeft ? ImageBlock : ContentBlock}
          {imageOnLeft ? ContentBlock : ImageBlock}
        </div>
      </div>
    </section>
  );
}

function ContactQuestionForm() {
  const [form, setForm] = useState({
    name: "",
    businessName: "",
    phone: "",
    email: "",
    message: "",
    consent: true,
  });
  const [submitting, setSubmitting] = useState(false);

  // Load tracking pixels once on mount; conversion fires only on success.
  useEffect(() => { initTracking(); }, []);

  const update = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    if (!form.consent) {
      toast.error("Please agree to be contacted by Swipetronics.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_BASE}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: form.businessName ? `FAQ inquiry — ${form.businessName}` : "FAQ inquiry",
        message: form.message,
        tracking: getTrackingPayload(),
      });
      trackConversion("ContactLead");
      pushLeadDataLayer("lead_contact_submit", "contact", r?.data?.id);
      toast.success("Thanks! A Swipetronics specialist will contact you shortly.");
      setForm({ name: "", businessName: "", phone: "", email: "", message: "", consent: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="section bg-white border-t border-line">
      <div className="container-swipe">
        <div className="grid lg:grid-cols-[1fr,1.3fr] gap-12 lg:gap-20 items-start">
          <Reveal variant="slide-left">
            <div>
              <div className="overline mb-4">Still curious?</div>
              <h2 className="font-display-tight text-[2rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.1]">
                Do You Have Any Questions?
              </h2>
              <p className="mt-5 text-[17px] text-slate-600 leading-[1.75] max-w-md">
                Send us a message and a Swipetronics specialist will contact you.
              </p>
              <div className="mt-10 hidden lg:block">
                <div className="rounded-2xl bg-[var(--swipe-navy)] text-white p-7 relative overflow-hidden">
                  <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-[var(--swipe-accent)]/25 blur-3xl" />
                  <div className="relative">
                    <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF]">
                      What happens next
                    </div>
                    <ul className="mt-4 space-y-3 text-[14.5px] text-white/85">
                      {[
                        "We review your request and the details provided.",
                        "A specialist reaches out, usually within one business day.",
                        "You get clear answers — no pressure.",
                      ].map((s, i) => (
                        <li key={s} className="flex items-start gap-3">
                          <span className="mt-1 w-5 h-5 rounded-full bg-[var(--swipe-accent)] text-white text-[11px] font-bold flex items-center justify-center shrink-0">
                            {i + 1}
                          </span>
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal variant="slide-right" delay={80}>
            <form
              onSubmit={submit}
              className="bg-white border border-line rounded-2xl p-7 lg:p-10 shadow-[0_30px_70px_-35px_rgba(6,26,58,0.25)]"
              data-testid="faq-contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="faq-name" className="text-[13px] font-semibold text-[var(--swipe-navy)]">Name</Label>
                  <Input
                    id="faq-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your full name"
                    className="mt-2 h-11"
                    data-testid="faq-input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="faq-business" className="text-[13px] font-semibold text-[var(--swipe-navy)]">Business Name</Label>
                  <Input
                    id="faq-business"
                    value={form.businessName}
                    onChange={update("businessName")}
                    placeholder="Your business"
                    className="mt-2 h-11"
                    data-testid="faq-input-business"
                  />
                </div>
                <div>
                  <Label htmlFor="faq-phone" className="text-[13px] font-semibold text-[var(--swipe-navy)]">Phone</Label>
                  <Input
                    id="faq-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(555) 123-4567"
                    className="mt-2 h-11"
                    data-testid="faq-input-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="faq-email" className="text-[13px] font-semibold text-[var(--swipe-navy)]">Email</Label>
                  <Input
                    id="faq-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@business.com"
                    className="mt-2 h-11"
                    data-testid="faq-input-email"
                  />
                </div>
              </div>
              <div className="mt-5">
                <Label htmlFor="faq-message" className="text-[13px] font-semibold text-[var(--swipe-navy)]">Message</Label>
                <Textarea
                  id="faq-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us what you're looking for..."
                  rows={5}
                  className="mt-2"
                  data-testid="faq-input-message"
                />
              </div>
              <label className="mt-6 flex items-start gap-3 text-[13px] text-slate-600 leading-relaxed cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={update("consent")}
                  className="mt-0.5 w-4 h-4 rounded border-line accent-[var(--swipe-accent)] cursor-pointer"
                  data-testid="faq-input-consent"
                />
                <span>
                  By submitting this form, you agree to be contacted by Swipetronics regarding
                  your request.
                </span>
              </label>
              <button
                type="submit"
                disabled={submitting}
                className="mt-7 btn-primary-swipe w-full justify-center text-[15px] py-3.5 disabled:opacity-60"
                data-testid="faq-submit-button"
              >
                {submitting ? "Sending..." : (<>Send Message <Send className="w-4 h-4" /></>)}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function FAQ() {
  return (
    <div data-testid="faq-page">
      <Seo {...getSeoForPath("/resources/faq")} />
      {/* HERO — unified: text + illustration blended into one navy header */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)",
          }}
        />
        <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
        <div aria-hidden="true" className="absolute top-[30%] right-[2%] w-2 h-2 rounded-full bg-[var(--swipe-accent)] opacity-70 pointer-events-none" />
        <div aria-hidden="true" className="absolute top-[18%] left-[40%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-60 pointer-events-none" />
        <div aria-hidden="true" className="absolute bottom-[20%] left-[8%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-50 pointer-events-none" />

        <ParticleField
          density={36}
          connectDistance={130}
          className="absolute inset-0 w-full h-full"
        />

        <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
          <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/resources" className="hover:text-white">Resources</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">FAQ</span>
          </nav>

          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-12 items-center">
            <Reveal>
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                  Help Center
                </div>
                <h1 className="font-display-tight text-[2.75rem] sm:text-[3.5rem] lg:text-[4.25rem] leading-[1.04] font-extrabold text-white tracking-[-0.025em]">
                  <span className="text-[var(--swipe-accent)]">FAQs</span>
                </h1>
                <p className="mt-6 text-[17px] text-white/75 leading-[1.7] max-w-xl">
                  Need help? Find clear answers about payment processing, POS systems, cash
                  discount programs, setup, support, and getting started with Swipetronics.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/contact"
                    className="btn-primary-swipe text-[15px] py-3.5 px-7"
                    data-testid="faq-hero-cta-contact"
                  >
                    Contact Support <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/savings-analysis"
                    className="btn-outline-light text-[15px] py-3.5 px-7"
                    data-testid="faq-hero-cta-savings"
                  >
                    Free Savings Analysis
                  </Link>
                </div>
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <ContactIllustration
                  transparent
                  className="relative w-full h-auto max-w-[560px] mx-auto"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* QUICK-JUMP NAV */}
      <section className="bg-[var(--swipe-accent-soft)]/60 border-b border-[var(--swipe-accent)]/15">
        <div className="container-swipe py-7">
          <Reveal>
            <div className="flex flex-wrap items-center gap-2.5 justify-center">
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--swipe-accent)] mr-2">
                <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />
                Jump to
              </span>
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="group inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[var(--swipe-accent)]/20 text-[13px] font-semibold text-[var(--swipe-navy)] hover:bg-[var(--swipe-accent)] hover:border-[var(--swipe-accent)] hover:text-white transition-all duration-200 shadow-[0_4px_12px_-6px_rgba(0,114,255,0.25)]"
                  data-testid={`faq-jump-${s.id}`}
                >
                  {s.title}
                  <ChevronRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTIONS — with navy accent stripes between groups */}
      {SECTIONS.map((section, i) => (
        <div key={section.id}>
          <FAQSection section={section} index={i} />
          {i === 1 && (
            <section className="bg-[var(--swipe-navy)] text-white relative overflow-hidden">
              <div className="absolute inset-0 grid-faint opacity-40 pointer-events-none" />
              <div className="absolute inset-0 pointer-events-none" style={{
                background: "radial-gradient(ellipse 50% 80% at 50% 50%, rgba(0,114,255,0.20) 0%, transparent 70%)"
              }} />
              <div className="container-swipe py-14 md:py-16 relative">
                <Reveal>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[var(--swipe-accent)] text-white flex items-center justify-center shrink-0 shadow-[0_12px_28px_-8px_rgba(0,114,255,0.6)]">
                        <Sparkles className="w-6 h-6" strokeWidth={2} />
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] font-extrabold" style={{ color: "#7DB6FF" }}>
                          Need a quick answer?
                        </div>
                        <div className="mt-1 font-display text-lg md:text-xl font-extrabold leading-snug" style={{ color: "#FFFFFF" }}>
                          A Swipetronics specialist can review your statement in under 10 minutes.
                        </div>
                      </div>
                    </div>
                    <Link
                      to="/savings-analysis"
                      className="btn-primary-swipe shrink-0"
                      data-testid="faq-stripe-cta"
                    >
                      Free Savings Analysis <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </Reveal>
              </div>
            </section>
          )}
        </div>
      ))}

      {/* CONTACT QUESTION FORM */}
      <ContactQuestionForm />

      {/* FINAL CTA */}
      <section className="bg-[var(--swipe-navy)] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-faint opacity-40 pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(ellipse 60% 50% at 50% 0%, rgba(0,114,255,0.22) 0%, transparent 65%)"
        }} />
        <div className="container-swipe section relative">
          <div className="max-w-4xl mx-auto text-center">
            <Reveal>
              <div className="overline mb-4" style={{ color: "#7DB6FF" }}>Let&apos;s talk</div>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="font-display-tight text-[2.25rem] sm:text-[2.75rem] lg:text-[3.5rem] font-extrabold leading-[1.08] text-white">
                Ready to Begin?
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-7 text-[17px] md:text-[18px] text-white/75 leading-[1.75] max-w-2xl mx-auto">
                Speak with Swipetronics to learn more about merchant processing, POS systems,
                cash discount options, terminals, and payment solutions that can help your
                business run smoother.
              </p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/contact"
                  className="btn-primary-swipe text-[15px] py-3.5 px-7"
                  data-testid="faq-cta-contact"
                >
                  Contact Us <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/get-started"
                  className="btn-outline-light text-[15px] py-3.5 px-7"
                  data-testid="faq-cta-get-started"
                >
                  Get Started
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
